import React, { useEffect, useState } from "react";
import { database, ref, push, onValue, update, set, remove } from "./firebase";
import { useNavigate } from "react-router-dom";
import "./BillingSystem.css";
import { useLocation } from "react-router-dom";
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

export default function RawStockPage({ role,redirectedFrom }) {
  const [rawProducts, setRawProducts] = useState([]);
  const [newProductName, setNewProductName] = useState("");
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (role !== "stock manager" && role !== "production"&& role !== "boss") return;

    const stockRef = ref(database, "rawStock");
    const historyRef = ref(database, "rawStockHistory");

    onValue(stockRef, (snapshot) => {
      const data = snapshot.val() || {};
      const formatted = Object.entries(data).map(([id, value]) => ({
        id,
        name: value.name,
        quantity: value.quantity || 0
      }));
      setRawProducts(formatted);
    });

    onValue(historyRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, value]) => ({
        id,
        ...value
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
      setHistory(list);
    });
  }, [role]);

  const handleAddProduct = async () => {
    if (!newProductName.trim()) return alert("Enter product name");
    const newRef = push(ref(database, "rawStock"));
    await set(newRef, {
      name: newProductName.trim(),
      quantity: 0
    });
    setNewProductName("");
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      await remove(ref(database, `rawStock/${id}`));
    }
  };

  const handleEditProduct = async (id, currentName) => {
    const newName = prompt("Enter new product name:", currentName);
    if (newName && newName.trim()) {
      await update(ref(database, `rawStock/${id}`), {
        name: newName.trim()
      });
    }
  };

  const logHistory = async (productName, type, quantity) => {
    const historyRef = push(ref(database, "rawStockHistory"));
    await set(historyRef, {
      product: productName,
      type,
      quantity,
      date: new Date().toISOString()
    });
  };

  const handleAddQuantity = async (id) => {
    const addQty = parseFloat(prompt("Enter quantity to add:"));
    if (isNaN(addQty) || addQty <= 0) return;
    const product = rawProducts.find(p => p.id === id);
    await update(ref(database, `rawStock/${id}`), {
      quantity: product.quantity + addQty
    });
    await logHistory(product.name, "Added", addQty);
  };

  const handleUseQuantity = async (id) => {
    const useQty = parseFloat(prompt("Enter quantity to use:"));
    if (isNaN(useQty) || useQty <= 0) return;
    const product = rawProducts.find(p => p.id === id);
    const newQty = product.quantity - useQty;
    if (newQty < 0) return alert("Not enough stock");
    await update(ref(database, `rawStock/${id}`), {
      quantity: newQty
    });
    await logHistory(product.name, "Used", useQty);
  };

  const handleEditHistoryQuantity = async (entry) => {
    const newQty = parseFloat(prompt("Enter new quantity:", entry.quantity));
    if (isNaN(newQty) || newQty <= 0) return;

    const difference = entry.type === "Added"
      ? newQty - entry.quantity
      : entry.quantity - newQty;

    const product = rawProducts.find(p => p.name === entry.product);
    if (!product) return alert("Product not found.");

    const updatedQty = product.quantity + (entry.type === "Added" ? difference : -difference);
    if (updatedQty < 0) return alert("Resulting stock would be negative.");

    await update(ref(database, `rawStock/${product.id}`), {
      quantity: updatedQty
    });

    await update(ref(database, `rawStockHistory/${entry.id}`), {
      quantity: newQty
    });
  };

  const handleDeleteHistoryEntry = async (entry) => {
    if (!window.confirm("Delete this history entry?")) return;

    const product = rawProducts.find(p => p.name === entry.product);
    if (!product) return alert("Product not found.");

    const newQty =
      entry.type === "Added"
        ? product.quantity - entry.quantity
        : product.quantity + entry.quantity;

    if (newQty < 0) return alert("Cannot delete, stock would go negative.");

    await update(ref(database, `rawStock/${product.id}`), {
      quantity: newQty
    });

    await remove(ref(database, `rawStockHistory/${entry.id}`));
  };

 if (role !== "stock manager" && role !== "production"&& role !== "boss") {
  return <p>Access denied. This page is for Stock Managers or Production only.</p>;
}

  return (
    <div className="container">
      <h2>Raw Stock Management</h2>
      {role === "boss" && <BossNavigation />}
      <Navigation role={role} />


      <div className="section">
        <input
          placeholder="New Raw Product Name"
          value={newProductName}
          onChange={(e) => setNewProductName(e.target.value)}
        />
        <button onClick={handleAddProduct}>Add Product</button>
      </div>

      <div className="section">
        <h4>Current Raw Stock</h4>
        <table className="stock-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Add</th>
              <th>Use</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {rawProducts.map((product) => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.quantity}</td>
                <td><button onClick={() => handleAddQuantity(product.id)}>+ Add</button></td>
                <td><button onClick={() => handleUseQuantity(product.id)}>- Use</button></td>
                <td><button onClick={() => handleEditProduct(product.id, product.name)}>✏️</button></td>
                <td><button onClick={() => handleDeleteProduct(product.id)}>🗑 Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section">
        <h4>Raw Stock History</h4>
        <table className="stock-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry, index) => (
              <tr key={index}>
                <td>{new Date(entry.date).toLocaleString()}</td>
                <td>{entry.product}</td>
                <td>{entry.type}</td>
                <td>{entry.quantity}</td>
                <td><button onClick={() => handleEditHistoryQuantity(entry)}>✏️</button></td>
                <td><button onClick={() => handleDeleteHistoryEntry(entry)}>🗑</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        
      </div>
    </div>
  );
}
